if (!customElements.get('noku-infinite-gallery')) {
  customElements.define('noku-infinite-gallery', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      const group = this.querySelector('.noku-gallery-group');
      const track = this.querySelector('.noku-gallery-track');
      if (!group || !track || !group.children.length) return;
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      const clone = group.cloneNode(true);
      clone.setAttribute('aria-hidden', 'true');

      for (const node of [clone, ...clone.querySelectorAll('*')]) {
        node.removeAttribute('id');
        node.removeAttribute('ref');
        node.removeAttribute('data-shopify-editor-block');
      }
      const makeInteractive = (card, duplicate) => {
        card.setAttribute('role', 'button');
        card.setAttribute('tabindex', duplicate ? '-1' : '0');
        card.setAttribute('aria-haspopup', 'dialog');
        card.setAttribute('aria-label', card.querySelector('img')?.alt || this.dataset.imageLabel);
      };
      [...group.children].forEach(card => makeInteractive(card, false));
      [...clone.children].forEach(card => makeInteractive(card, true));
      track.append(clone);
      this.clone = clone;
      const viewport = this.querySelector('.noku-gallery-viewport');
      const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
      const render = () => {
        if (!this.distance) return;
        this.offset = ((this.offset % this.distance) + this.distance) % this.distance;
        track.style.transform = 'translate3d(' + -this.offset + 'px,0,0)';
      };
      let hover = false;
      let visible = true;
      let gesture = null;
      let tween = null;
      let suppressClick = false;
      let lastTime = 0;
      this.offset = 0;
      const frame = time => {
        const elapsed = lastTime ? Math.min(time - lastTime, 50) : 0;
        lastTime = time;
        if (tween) {
          const progress = Math.min((time - tween.start) / tween.duration, 1);
          const eased = 1 - Math.pow(1 - progress, 3);
          this.offset += tween.delta * (eased - tween.progress);
          tween.progress = eased;
          if (progress === 1) tween = null;
        } else if (!hover && !gesture && visible && !document.hidden && !reducedMotion.matches &&
                   !this.hasAttribute('lightbox-open') && !this.matches(':has(:focus-visible)')) {
          this.offset += elapsed * 0.024;
        }
        render();
        this.frame = requestAnimationFrame(frame);
      };
      this.frame = requestAnimationFrame(frame);
      this.addEventListener('pointerenter', event => {
        if (event.pointerType === 'mouse') hover = true;
      }, options);
      this.addEventListener('pointerleave', event => {
        if (event.pointerType === 'mouse') hover = false;
      }, options);
      viewport.addEventListener('dragstart', event => event.preventDefault(), options);
      viewport.addEventListener('pointerdown', event => {
        if (!event.isPrimary || event.button !== 0) return;
        tween = null;
        suppressClick = false;
        gesture = { id: event.pointerId, x: event.clientX, y: event.clientY, lastX: event.clientX, startOffset: this.offset, moved: 0, axis: null };
        this.setAttribute('touched', '');
      }, options);
      window.addEventListener('pointermove', event => {
        if (!gesture || event.pointerId !== gesture.id) return;
        const dx = event.clientX - gesture.x;
        const dy = event.clientY - gesture.y;
        if (!gesture.axis && Math.max(Math.abs(dx), Math.abs(dy)) > 8) {
          gesture.axis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
          if (gesture.axis === 'x') {
            suppressClick = true;
            viewport.setPointerCapture(event.pointerId);
          }
        }
        if (gesture.axis === 'x') {
          event.preventDefault();
          const movement = gesture.lastX - event.clientX;
          this.offset += movement;
          gesture.moved += movement;
          render();
        }
        gesture.lastX = event.clientX;
      }, { ...options, passive: false });
      const slideStep = () => group.children[0].getBoundingClientRect().width +
        (parseFloat(getComputedStyle(this).getPropertyValue('--carousel-gap')) || 24);
      const release = event => {
        if (!gesture || event.pointerId !== gesture.id) return;
        if (event.type === 'lostpointercapture' && event.target !== viewport) return;
        if (event.type === 'lostpointercapture' && viewport.hasPointerCapture(event.pointerId)) return;
        const completed = gesture;
        gesture = null;
        this.removeAttribute('touched');
        if (viewport.hasPointerCapture(event.pointerId)) viewport.releasePointerCapture(event.pointerId);
        if (completed.axis === 'x' && event.type !== 'pointercancel') {
          const step = slideStep();
          const distance = completed.moved;
          const slides = Math.abs(distance) >= 24 ? Math.max(1, Math.round(Math.abs(distance) / step)) : 0;
          const targetDistance = Math.sign(distance) * slides * step;
          const delta = targetDistance - distance;
          if (reducedMotion.matches) {
            this.offset += delta;
            render();
          } else {
            tween = { start: performance.now(), delta, progress: 0, duration: 420 };
          }
        }
      };
      window.addEventListener('pointerup', release, options);
      window.addEventListener('pointercancel', release, options);
      viewport.addEventListener('lostpointercapture', release, options);
      const advance = direction => {
        const step = slideStep();
        tween = reducedMotion.matches ? null : { start: performance.now(), delta: direction * step, progress: 0, duration: 320 };
        if (reducedMotion.matches) {
          this.offset += direction * step;
          render();
        }
      };
      this.querySelectorAll('[data-gallery-direction]').forEach(button => {
        button.addEventListener('click', () => advance(Number(button.dataset.galleryDirection)), options);
      });
      viewport.addEventListener('keydown', event => {
        if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
          event.preventDefault();
          advance(event.key === 'ArrowRight' ? 1 : -1);
        }
      }, options);
      const measure = () => {
        const styles = getComputedStyle(this);
        const gap = parseFloat(styles.getPropertyValue('--carousel-gap')) || 24;
        const columns = parseFloat(styles.getPropertyValue(innerWidth < 750 ? '--carousel-mobile-columns' : '--carousel-columns')) || 1;
        this.style.setProperty('--noku-card-width', (innerWidth < 750 ? this.clientWidth * 0.75 : (this.clientWidth - gap * (columns - 1)) / columns) + 'px');
        const distance = group.getBoundingClientRect().width;
        this.style.setProperty('--noku-distance', distance + 'px');
        this.distance = distance;
        this.offset = this.offset || 0;
        render();
        this.setAttribute('ready', '');
      };
      this.resize = new ResizeObserver(measure);
      this.resize.observe(this);
      this.visibility = new IntersectionObserver(entries => {
        visible = entries[0].isIntersecting;
        this.toggleAttribute('out-of-view', !visible);
      });
      this.visibility.observe(this);
      const hidden = () => this.toggleAttribute('paused-hidden', document.hidden);
      document.addEventListener('visibilitychange', hidden, options);
      const dialog = this.querySelector('.noku-gallery-dialog');
      const large = dialog.querySelector('.noku-gallery-large');
      const open = card => {
        const image = card.querySelector('img');
        if (!image) return;
        large.src = image.dataset.nokuFullImage || image.src;
        large.alt = image.alt;
        this.setAttribute('lightbox-open', '');
        dialog.showModal();
      };
      track.addEventListener('click', event => {
        if (suppressClick) {
          suppressClick = false;
          event.preventDefault();
          return;
        }
        const card = event.target.closest('.card');
        if (card) open(card);
      }, options);
      track.addEventListener('keydown', event => {
        const card = event.target.closest('.card');
        if (card && (event.key === 'Enter' || event.key === ' ')) {
          event.preventDefault();
          open(card);
        }
      }, options);
      dialog.querySelector('button').addEventListener('click', () => dialog.close(), options);
      dialog.addEventListener('click', event => {
        const box = dialog.getBoundingClientRect();
        if (event.target === dialog && (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom)) dialog.close();
      }, options);
      dialog.addEventListener('close', () => this.removeAttribute('lightbox-open'), options);
      measure();
    }
    disconnectedCallback() {
      cancelAnimationFrame(this.frame);
      this.controller?.abort();
      this.resize?.disconnect();
      this.visibility?.disconnect();
      this.clone?.remove();
      this.controller = null;
    }
  });
}
